import React, { useEffect } from "react";
import { UseFormReturn } from "react-hook-form";

import { ZodProvider } from "@autoform/zod";
import {
  useNotification,
  useParsed,
  useWarnAboutChange,
} from "@refinedev/core";
import { useDocumentTitle } from "@refinedev/react-router";

import { BottomActionBar } from "@/components/bottom-action-bar";
import { AutoForm } from "@/components/ui/autoform";
import { injectCustomAutoFormComponents } from "@/components/ui/autoform/components/custom";
import { Button } from "@/components/ui/button";
import { useLoadingOverlay } from "@/contexts/loading-context";
import { toHumanErrorMessage } from "@/lib/extensions/httpErrorExtension";
import { getDirtyValues, isFormDirty, withClearedFields } from "@/lib/utils";
import { parseRead } from "@/lib/zod/read-schema";
import { useShowGenreRepository } from "@/pages/show-genres/hooks/use-show-genre-repository";
import {
  ShowGenreEditFormType,
  showGenreEditSchema,
  toShowGenreForm,
} from "@/pages/show-genres/schemas/show-genre.edit.form";

const schemaProvider = new ZodProvider(showGenreEditSchema);

/** `UpdateShowGenreCommand.About` accepts and stores `""`; `name` is required and stays out. */
const CLEARABLE_FIELDS = ["about"];

/**
 * Edit one show genre — the cinema/theater genre vocabulary. A rename is written through to
 * every movie, play, performance and screening that carries the genre, by the backend.
 *
 * No delete button, unlike `MoviesEdit`: `DeleteShowGenreCommand` removes the document and
 * nothing else, so every movie and play carrying the genre would keep a dangling ref.
 */
export const ShowGenresEdit = () => {
  const { get, update } = useShowGenreRepository();
  const { id } = useParsed();
  const [values, setValues] = React.useState<ShowGenreEditFormType>();
  const { open } = useNotification();
  const { setLoading } = useLoadingOverlay();
  const { setWarnWhen, warnWhen } = useWarnAboutChange();
  const setTitle = useDocumentTitle();

  useEffect(() => {
    if (!id) {
      open?.({
        type: "error",
        message: "Something went wrong. This shouldn't be happening",
      });
      return;
    }
    setLoading(true);
    get(id.toString())
      .then((response) => {
        const data = parseRead(
          showGenreEditSchema,
          toShowGenreForm(response),
          "show genre",
        ) as ShowGenreEditFormType;
        setValues(data);
        setTitle("Edit " + (data.name ?? "show genre"));
      })
      .catch((error) => {
        console.error(error);
        open?.({
          type: "error",
          message: "Failed to get show genre",
          description: toHumanErrorMessage(error),
        });
      })
      .finally(() => setLoading(false));
  }, [id]);

  const initializeForm = (
    form: UseFormReturn<ShowGenreEditFormType, any, ShowGenreEditFormType>,
  ) => {
    form.watch((value) => {
      setWarnWhen(
        form.formState.isDirty &&
          isFormDirty(
            form.formState.dirtyFields as Record<string, unknown>,
            value as Record<string, unknown>,
            form.formState.defaultValues as Record<string, unknown>,
          ),
      );
    });
  };

  const onSubmit = async (
    allValues: ShowGenreEditFormType,
    form: UseFormReturn<ShowGenreEditFormType, any, ShowGenreEditFormType>,
  ) => {
    const changed = getDirtyValues(
      form.formState.dirtyFields as Record<string, unknown>,
      allValues,
    );
    // AutoForm strips emptied fields before we get here — re-add a cleared `about` as "".
    const dirty = withClearedFields<ShowGenreEditFormType>(
      changed,
      form.getValues() as Record<string, unknown>,
      form.formState.defaultValues as Record<string, unknown>,
      CLEARABLE_FIELDS,
    );
    setWarnWhen(false);
    await update({ id: allValues.id, ...dirty });
  };

  return (
    <div className="flex flex-row">
      <div className="flex w-full flex-col space-y-4 p-8">
        {values && (
          <AutoForm
            schema={schemaProvider}
            defaultValues={values}
            onSubmit={onSubmit}
            onFormInit={initializeForm}
            {...injectCustomAutoFormComponents()}
          >
            <BottomActionBar>
              <Button disabled={!warnWhen} type="submit">
                Submit
              </Button>
            </BottomActionBar>
          </AutoForm>
        )}
      </div>
    </div>
  );
};
